import type {
	DocumentCopyBucketModel,
	DocumentCopyConfigModel,
} from '../models/dto/document-copy-config.dto';
import { CopyMode } from '../models/entity/document-copy-recipient.entity';

/**
 * PD-16841 — a customer document's recipients as the settings screen edits
 * them: one list, and one choice of which line of the email (To, CC or BCC)
 * everyone on it goes on.
 *
 * Storage keeps a bucket per line (document_copy_recipient.copyMode on every
 * row), so these two helpers translate between the stored buckets and the
 * screen's single list. Kept pure and shared so the screen and its tests
 * agree on how a stored configuration reads.
 */
export interface DocumentCopyListModel {
	mode: CopyMode;
	bucket: DocumentCopyBucketModel;
}

/**
 * The line a list uses before anyone has chosen one. BCC, because that is
 * what the rules defaulted to before the choice existed (the entity's column
 * default) and it never shows the tenant's own staff to the customer.
 */
export const DEFAULT_DOCUMENT_COPY_MODE = CopyMode.BCC;

/** The lines in the order the resolver lets them win: the more visible first. */
const MODE_PRECEDENCE: ReadonlyArray<CopyMode> = [
	CopyMode.TO,
	CopyMode.CC,
	CopyMode.BCC,
];

const isEmpty = (bucket: DocumentCopyBucketModel): boolean =>
	bucket.roleIds.length === 0 &&
	bucket.userIds.length === 0 &&
	bucket.emailAddresses.length === 0;

const emptyBucket = (): DocumentCopyBucketModel => ({
	roleIds: [],
	userIds: [],
	emailAddresses: [],
});

const bucketFor = (
	config: Partial<Pick<DocumentCopyConfigModel, 'to' | 'cc' | 'bcc'>>,
	mode: CopyMode,
): DocumentCopyBucketModel => {
	switch (mode) {
		case CopyMode.TO:
			return config.to ?? emptyBucket();
		case CopyMode.CC:
			return config.cc ?? emptyBucket();
		case CopyMode.BCC:
			return config.bcc ?? emptyBucket();
	}
};

/**
 * Read stored buckets as one list. The list's line is the most visible line
 * anything is stored on; everyone stored on any line is in the list, so
 * saving it back moves a mixed configuration onto that one line rather than
 * dropping anyone. (No tenant had a mixed list when the choice was added.)
 */
export function toDocumentCopyList(
	config:
		| Partial<Pick<DocumentCopyConfigModel, 'to' | 'cc' | 'bcc'>>
		| undefined,
): DocumentCopyListModel {
	const stored = config ?? {};
	const buckets = MODE_PRECEDENCE.map((mode) => bucketFor(stored, mode));
	const mode =
		MODE_PRECEDENCE.find((candidate) => !isEmpty(bucketFor(stored, candidate))) ??
		DEFAULT_DOCUMENT_COPY_MODE;

	const addresses = new Map<string, string>();
	for (const bucket of buckets) {
		for (const address of bucket.emailAddresses) {
			const key = address.trim().toLowerCase();
			if (key !== '' && !addresses.has(key)) {
				addresses.set(key, address.trim());
			}
		}
	}

	return {
		mode,
		bucket: {
			roleIds: [...new Set(buckets.flatMap((b) => b.roleIds))],
			userIds: [...new Set(buckets.flatMap((b) => b.userIds))],
			emailAddresses: [...addresses.values()],
		},
	};
}

/** Split one list back into the stored buckets: everyone on the chosen line. */
export function fromDocumentCopyList(
	list: DocumentCopyListModel,
): Pick<DocumentCopyConfigModel, 'to' | 'cc' | 'bcc'> {
	return {
		to: list.mode === CopyMode.TO ? list.bucket : emptyBucket(),
		cc: list.mode === CopyMode.CC ? list.bucket : emptyBucket(),
		bcc: list.mode === CopyMode.BCC ? list.bucket : emptyBucket(),
	};
}

/**
 * PD-17100 — whether two lists name the same people on the same line, so a
 * screen saves only a list someone actually changed. Order does not matter
 * (reordering chips is not a change), and addresses compare case-insensitively
 * the way the resolver treats them.
 */
export function isSameDocumentCopyList(
	a: DocumentCopyListModel,
	b: DocumentCopyListModel,
): boolean {
	const key = (list: DocumentCopyListModel): string =>
		[
			list.mode,
			[...list.bucket.roleIds].sort((x, y) => x - y).join(','),
			[...list.bucket.userIds].sort((x, y) => x - y).join(','),
			[...new Set(list.bucket.emailAddresses.map((e) => e.trim().toLowerCase()))]
				.filter((e) => e !== '')
				.sort()
				.join(','),
		].join('|');

	return key(a) === key(b);
}
